import BreadCrumb from "Common/BreadCrumb";
import { axios_helper_async } from "helpers/Axios";
import { Brands } from "Common/Models/Brands";
import { Category } from "Common/Models/Category";
import { MessageTypes } from "Common/constants/MessageEnums";
import withMessages from "Common/withMessages";
import withNotifications, {
    WithNotificationsProps,
} from "Common/withNotifications";
import withOverlay, { WithOverlayProps } from "Common/withOverlay";
import {
    Car,
    FileText,
    Link,
    Minus,
    Plus,
    Search,
    ShoppingCart,
    Store,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { WithMessagesProps } from "../../Common/withMessages";
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import withTranslation from "Common/withTranslation";
import Card from "Common/Components/Card/Card";
import product1 from "assets/images/Index.resources/SpareParts_2.jpg";
import OrderByAlfaNumeric from "Common/constants/CategoryNames";
import { Product } from "Common/Models/Products";
import withRouter, { WithRouterProps } from "Common/withRouter";
import TableContainer from "Common/TableContainer";
import { reactTableData } from "Common/data";
import { column } from "pages/Components/Table/ReactTable";
import Title from "Common/Components/Titles/Title";
import { GetCurrencyChange } from "helpers/Currencies/CurrencyChange";
// import { ProductDetails } from "Common/Models/ProductDetails";
import { NotificationType } from "Common/Components/Notifications/notificationTypes";
import { WithCurrencyProps } from "Common/Components/Currency/WithCurrencyProps";
import { withCurrency } from "Common/Components/Currency/withCurrency";
import ProductRequest from "./ProductRequest";
import { CartProduct } from "Common/Models/CartProduct";
import withAuth from "Common/Components/Authentication/withAuth";
import { WithAuthProps } from "Common/Components/Authentication/WithAuthProps";
import { WithCartProps } from "Common/Components/Cart/WithCartProps";
import { withCart } from "Common/Components/Cart/withCart";
import { ProductDetails } from "Common/Models/ProductDetails";
import { ProductStatus, Units, WeightUnits } from "Common/constants/Enums";

const ProductPage: React.FC<
    WithMessagesProps &
    WithNotificationsProps &    
    WithTranslationProps &
    WithRouterProps &
    WithCurrencyProps &
    WithAuthProps &
    WithCartProps
> = ({
    messages,
    notifications,    
    i18n,
    t,
    router,
    c,
    account,
    cart,
}) => {
        const initialProduct: ProductDetails = {
            brand: '',
            categoryName: '',
            datasheetURL: '',
            deliveryTime: 0,
            deliveryUnit: 0,
            description: '',
            id: 0,
            name: '',
            price: 0,
            quantity: 0,
            sku: '',
            status: ProductStatus.Obsolete,
            unit: Units.Pices,
            weight: 0,
            weightUnit: WeightUnits.kg,
        }
        const [product, setProduct] = useState<ProductDetails>(initialProduct);
        const [isRequestOpen, setIsRequestOpen] = useState<boolean>(false);
        const [quantity, setQuantity] = useState<number>(1);
        const [productId, setProductId] = useState<number>(0);
        const [isLoading, setIsLoading] = useState<boolean>(false);

        const GetProduct = async (_productId?: number) => {
            try {
                setIsLoading(true);
                if(productId > 0){
                    const body = {
                        language: i18n.language,
                        productId: _productId ? _productId : productId,
                    };
                    let response = await axios_helper_async<ProductDetails>(
                        "/SOI/Get-Product-Details-By-Id",
                        "GET",
                        body
                    );
        
                    messages.setMessages(response.messages);
                    notifications.setNotifications(response.notifications);
        
                    if (response.isSuccess && response.data) {
                        setProduct(response.data);
                    }
                }
            }
            catch (err: any) {
                notifications.showNotification(err, NotificationType.Error);
            }
            finally {
                setIsLoading(false);
            }
        };

        const countUp = () => {
            if (product) {
                const newValue = quantity + 1;
                setQuantity(newValue <= product.quantity ? newValue : product.quantity);
            }
        };

        const countDown = () => {
            if (product) {
                const newValue = quantity - 1;
                setQuantity(
                    newValue <= product.quantity
                        ? newValue > 0
                            ? newValue
                            : 1
                        : product.quantity
                );
            }
        };

        const SetCartProduct = async () => {
            try {
                const body: CartProduct = {
                    // id: 0,
                    customerId: Number(account.id),
                    productId: product.id,
                    quantity: quantity,
                };

                let response = await axios_helper_async<boolean>(
                    "/Cart/Set-Cart-Product",
                    "POST",
                    body
                );

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if (response.isSuccess) {
                    if (response.data) {
                        notifications.showNotification(
                            "Product.Cart.Success",
                            NotificationType.Success
                        );
                    } else {
                        notifications.showNotification(
                            "Product.Cart.Error",
                            NotificationType.Error
                        );
                    }
                }
            } catch (err: any) {
                notifications.showNotification(err, NotificationType.Error);
            } finally {
                setQuantity(1);
                cart.updateCart();
            }
        };

        const downloadDatasheet = async (name?: string) => {
            try {
                if (name) {
                    const body = { name: name };

                    let response = await axios_helper_async<Blob>(
                        "/SOI/Get-Product-Datasheet",
                        "GET",
                        body,
                        { responseType: "blob" }
                    );

                    messages.setMessages(response.messages);
                    notifications.setNotifications(response.notifications);

                    if (response.isSuccess && response.data) {
                        // Crea un Blob a partir de la respuesta
                        const blob = new Blob([response.data], { type: "application/pdf" });

                        if (blob.size > 0) {
                            // Crea un link de descarga y simula el click
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            link.setAttribute("download", `Datasheet_${product?.sku}.pdf`); // Nombre del archivo
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode?.removeChild(link);
                        } else {
                            notifications.showNotification(
                                "El archivo está vacío.",
                                NotificationType.Error
                            );
                        }
                    } else {
                        notifications.showNotification(
                            "No se encontró el archivo del datasheet.",
                            NotificationType.Error
                        );
                    }
                }
            } catch (err: any) {
                notifications.showNotification(
                    err.message || "Error al descargar el archivo.",
                    NotificationType.Error
                );
            } finally {
            }
        };

        useEffect(() => {
            const _productId = router.queryParams.get("product");

            setProductId(Number(_productId));

            i18n.on("languageChanged", async () => await GetProduct(productId));

            return () => {
                i18n.off("languageChanged", async () => await GetProduct(productId));
            };
        }, []);

        useEffect(() => {     
            (async () => {
                await GetProduct();
            })();
        }, [productId]);

        return (
            <React.Fragment>
                <div className="flex flex-col justify-center items-center w-full gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 justify-start items-start gap-2 p-3 w-full">
                        <div className="flex flex-col w-full justify-center items-center text-center gap-2">
                            {/* <h3 className="">{t("Product.Model Title")}</h3> */}
                            <Title text={t("Product.ModelTitle")} />
                            <h5>{product?.sku}</h5>
                            <img
                                className="shadow-md"
                                width={300}
                                height={300}
                                src={product?.image}
                            ></img>
                            <span className="text-red-400 text-sm italic">
                                *{t("Product.ImageLegend")}*
                            </span>
                        </div>
                        <div className="flex flex-col justify-center items-center gap-2 p-3 w-full">
                            {/* <h3 className="">Información del articulo</h3>                         */}
                            <Title text={t("Product.InfoProductTitle")} />
                            <div className="table-container">
                                <table className="productTable">
                                    <tbody>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.Name")}:</td>
                                            <td>{product?.name}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.Category")}:</td>
                                            {/* <td>{categoryId}</td> */}
                                            <td>{product.categoryName}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.Description")}:</td>
                                            <td>{product?.description}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.Brand")}:</td>
                                            <td>{product?.brand}</td>
                                        </tr>
                                        <tr
                                            className={`${product?.price === 0 ? "text-red-500" : ""}`}
                                        >
                                            <td>{t("Product.InfoProductTable.Price")}:</td>
                                            <td>
                                                {product?.price === 0
                                                    ? t("Product.Errors.Price")
                                                    : `$${c(product?.price ?? 0)}`}
                                            </td>
                                        </tr>
                                        <tr
                                            className={`${product?.quantity === 0 ? "text-red-500" : ""
                                                }`}
                                        >
                                            <td>{t("Product.InfoProductTable.Stock")}:</td>
                                            <td>{product?.quantity}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.Units")}:</td>
                                            <td>{product?.unit}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.Weight")}:</td>
                                            <td>{product?.weight}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.DeliveryTime")}:</td>
                                            <td>{product?.deliveryTime}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.Status")}:</td>
                                            <td>{product?.status}</td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.DatasheetURL")}:</td>
                                            <td>
                                                <a
                                                    className="flex flex-row gap-2 text-custom-400 underline italic"
                                                    href={
                                                        product?.datasheetURL?.startsWith("http")
                                                            ? product.datasheetURL
                                                            : `https://${product?.datasheetURL}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {product?.datasheetURL}
                                                    <Link size={15} />
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>{t("Product.InfoProductTable.DatasheetFile")}:</td>
                                            <td>
                                                <span
                                                    className="cursor-pointer text-custom-400 underline italic"
                                                    onClick={() => downloadDatasheet(product?.name)}
                                                >
                                                    {t("Product.InfoProductTable.Datasheet")}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex flex-row justify-center md:justify-end items-center w-full gap-3">
                                {!product?.quantity || !product?.price ? (
                                    <button
                                        className="btn btn-red flex flex-row gap-2 justify-center items-center"
                                        onClick={() => setIsRequestOpen(true)}
                                    >
                                        <FileText></FileText>
                                        {t("Product.RequestAQuote")}
                                    </button>
                                ) : null}
                                {product?.quantity && product?.price ? (
                                    <div className="inline-flex p-2 text-center border rounded input-step border-slate-200 dark:border-zink-500">
                                        <button
                                            type="button"
                                            className="border w-7 leading-[15px] minus-value bg-slate-200 dark:bg-zink-600 dark:border-zink-600 rounded transition-all duration-200 ease-linear border-slate-200 text-slate-500 dark:text-zink-200 hover:bg-custom-500 dark:hover:bg-custom-500 hover:text-custom-50 dark:hover:text-custom-50 hover:border-custom-500 dark:hover:border-custom-500 focus:bg-custom-500 dark:focus:bg-custom-500 focus:border-custom-500 dark:focus:border-custom-500 focus:text-custom-50 dark:focus:text-custom-50"
                                            onClick={() => countDown()}
                                        >
                                            <Minus className="inline-block w-4 h-4" />
                                        </button>
                                        <input
                                            type="number"
                                            className="text-center ltr:pl-2 rtl:pr-2 w-15 h-7 products-quantity dark:bg-zink-700 focus:shadow-none"
                                            value={quantity}
                                            min={1}
                                            max={product.quantity}
                                            readOnly
                                        />
                                        <button
                                            type="button"
                                            className="transition-all duration-200 ease-linear border rounded border-slate-200 bg-slate-200 dark:bg-zink-600 dark:border-zink-600 w-7 plus-value text-slate-500 dark:text-zink-200 hover:bg-custom-500 dark:hover:bg-custom-500 hover:text-custom-50 dark:hover:text-custom-50 hover:border-custom-500 dark:hover:border-custom-500 focus:bg-custom-500 dark:focus:bg-custom-500 focus:border-custom-500 dark:focus:border-custom-500 focus:text-custom-50 dark:focus:text-custom-50"
                                            onClick={() => countUp()}
                                        >
                                            <Plus className="inline-block w-4 h-4" />
                                        </button>
                                    </div>
                                ) : null}
                                {product?.quantity && product?.price ? (
                                    <button
                                        className="btn btn-custom flex flex-row gap-2 justify-center items-center"
                                        onClick={SetCartProduct}
                                    >
                                        <ShoppingCart></ShoppingCart>
                                        {t("Product.AddToCart")}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    {isRequestOpen ? (
                        <ProductRequest
                            product={product}
                            Close={() => setIsRequestOpen(false)}
                        ></ProductRequest>
                    ) : null}
                </div>
            </React.Fragment>
        );
    };

export default withTranslation(withNotifications(withMessages(withRouter(withCurrency(withAuth(withCart(ProductPage)))))));
